import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useLayoutEffect, forwardRef, useRef, } from 'react';
import { Box, VStack, Text, HStack, Popover, PopoverContent, PopoverAnchor, Portal, } from '@chakra-ui/react';
// The component now manages its own Popover state via props
const MentionSuggestionList = forwardRef((props, ref // This ref is for the listbox itself, for keyboard navigation scrolling
) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const anchorRef = useRef(null); // Ref for the invisible PopoverAnchor
    useEffect(() => {
        // Update position of the invisible anchor when targetRect changes
        if (anchorRef.current && props.targetRect) {
            anchorRef.current.style.position = 'fixed'; // Use fixed to position relative to viewport
            anchorRef.current.style.left = `${props.targetRect.left}px`;
            anchorRef.current.style.top = `${props.targetRect.bottom}px`; // Position below the caret/mention
            anchorRef.current.style.width = '0px';
            anchorRef.current.style.height = '0px';
            anchorRef.current.style.opacity = '0';
            anchorRef.current.style.pointerEvents = 'none'; // Make it non-interactive
        }
    }, [props.targetRect]);
    const selectItem = useCallback((index) => {
        const item = props.items[index];
        if (item) {
            props.command({ id: item.id, label: item.label });
        }
    }, [props]);
    // Keyboard navigation logic (remains largely the same)
    useEffect(() => {
        if (!props.isOpen || props.items.length === 0)
            return;
        const navigationKeys = ['ArrowUp', 'ArrowDown', 'Enter', 'Escape']; // Escape handled by Popover usually, but good for safety
        const onKeyDown = (e) => {
            if (navigationKeys.includes(e.key)) {
                e.preventDefault();
                e.stopPropagation(); // Prevent Tiptap from handling these when list is open
                if (e.key === 'ArrowUp') {
                    setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
                    return true;
                }
                if (e.key === 'ArrowDown') {
                    setSelectedIndex((selectedIndex + 1) % props.items.length);
                    return true;
                }
                if (e.key === 'Enter') {
                    selectItem(selectedIndex);
                    return true;
                }
                // Escape might be handled by Popover itself if closeOnEsc is true
            }
            return false;
        };
        document.addEventListener('keydown', onKeyDown, true); // Use capture phase for early intervention
        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
        };
    }, [props.isOpen, props.items, selectedIndex, selectItem]);
    useLayoutEffect(() => {
        if (!props.isOpen)
            setSelectedIndex(0); // Reset index when popover closes/reopens
    }, [props.isOpen]);
    // Scroll to selected item
    useLayoutEffect(() => {
        if (!props.isOpen)
            return;
        const listbox = ref && typeof ref !== 'function' && ref.current;
        if (listbox) {
            const element = listbox.children[selectedIndex];
            if (element) {
                element.scrollIntoView?.({ block: 'nearest' });
            }
        }
    }, [selectedIndex, ref, props.isOpen]);
    if (!props.items || props.items.length === 0) {
        // Popover won't open if isOpen is false or if there's no content by default
        // but this ensures we don't try to render an empty list inside PopoverContent
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsx(Box, { ref: anchorRef }), _jsxs(Portal, { children: [' ', _jsxs(Popover, { isOpen: props.isOpen, placement: "bottom-start" // Desired placement
                        , isLazy: true, gutter: 2, closeOnBlur: true, closeOnEsc: true, children: [_jsx(PopoverAnchor, { children: _jsx("div", { style: {
                                        position: 'fixed',
                                        top: props.targetRect?.bottom,
                                        left: props.targetRect?.left,
                                    } }) }), _jsx(PopoverContent
                            // ref={ref} // The main ref is for the scrollable listbox inside
                            , { 
                                // ref={ref} // The main ref is for the scrollable listbox inside
                                p: 0, bg: "gray.700", borderRadius: "md", boxShadow: "lg", borderWidth: 1, borderColor: "gray.600", maxH: "300px", overflowY: "auto", w: "auto" // Fit content width
                                , minW: "200px", children: _jsxs(Box, { ref: ref, p: 1, children: [' ', _jsx(VStack, { align: "stretch", spacing: 1, children: props.items.map((item, index) => (_jsxs(HStack, { p: 2, borderRadius: "sm", bg: index === selectedIndex
                                                    ? 'blue.500'
                                                    : 'transparent', color: index === selectedIndex
                                                    ? 'white'
                                                    : 'whiteAlpha.800', _hover: {
                                                    bg: index === selectedIndex
                                                        ? 'blue.500'
                                                        : 'whiteAlpha.100',
                                                }, cursor: "pointer", onClick: () => selectItem(index), id: `mention-item-${index}`, children: [typeof item.icon === 'string' ? (_jsx(Text, { fontSize: "sm", mr: 2, children: item.icon })) : (item.icon), _jsx(Text, { fontWeight: index === selectedIndex
                                                            ? 'semibold'
                                                            : 'normal', children: item.label })] }, item.id))) })] }) })] })] })] }));
});
MentionSuggestionList.displayName = 'MentionSuggestionList';
export default MentionSuggestionList;
//# sourceMappingURL=MentionSuggestionList.js.map